import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Calculator, TrendingUp, ArrowRight } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { useI18n } from "../../i18n/i18n";
import CompoundInterestChart from "../charts/CompoundInterestChart";
import CurrencySelector from "../CurrencySelector";
import { generateCompoundInterestPDF } from "../../lib/pdfExport";
import { useAuthCheck } from "../../hooks/useAuthCheck";
import { useAuth } from "../AuthProvider";
import { AuthModal } from "../AuthModal";
import { LanguageLink } from "../LanguageLink";

interface CompoundInterestState {
  initialInvestment: number;
  monthlyContribution: number;
  interestRate: number;
  years: number;
  compoundFrequency: number;
}

interface CalculationResults {
  initialInvestment: number;
  totalContributions: number;
  interestEarned: number;
  finalBalance: number;
  yearlyData: Array<{
    year: number;
    principal: number;
    balance: number;
  }>;
}

const CompoundInterestCalculator = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [currency, setCurrency] = useState<string>("EUR");
  const { 
    checkAuthAndExecute, 
    isCheckingAuth, 
    showAuthModal, 
    setShowAuthModal, 
    pendingCallback 
  } = useAuthCheck();
  // Store original results in the base currency
  const [originalResults, setOriginalResults] =
    useState<CalculationResults | null>(null);
  // Track original currency
  const [originalCurrency, setOriginalCurrency] = useState<string>("EUR");

  const eurToUsdRate = 1.08; // 1 EUR = 1.08 USD
  const usdToEurRate = 0.93; // 1 USD = 0.93 EUR

  const convertAmount = (
    amount: number,
    fromCurrency: string,
    toCurrency: string,
  ): number => {
    if (fromCurrency === toCurrency) return amount;
    return fromCurrency === "EUR"
      ? amount * eurToUsdRate
      : amount * usdToEurRate;
  };

  const handleCurrencyChange = (newCurrency: string) => {
    // Allow currency change regardless of calculation results
    if (originalResults && results.finalBalance !== 0) {
      // If returning to original currency, use original results
      if (newCurrency === originalCurrency) {
        setResults(originalResults);
      } else {
        // Convert from original results to new currency
        const newResults = {
          ...originalResults,
          initialInvestment: convertAmount(
            originalResults.initialInvestment,
            originalCurrency,
            newCurrency,
          ),
          totalContributions: convertAmount(
            originalResults.totalContributions,
            originalCurrency,
            newCurrency,
          ),
          interestEarned: convertAmount(
            originalResults.interestEarned,
            originalCurrency,
            newCurrency,
          ),
          finalBalance: convertAmount(
            originalResults.finalBalance,
            originalCurrency,
            newCurrency,
          ),
          yearlyData: originalResults.yearlyData.map((data) => ({
            year: data.year,
            principal: convertAmount(
              data.principal,
              originalCurrency,
              newCurrency,
            ),
            balance: convertAmount(data.balance, originalCurrency, newCurrency),
          })),
        };
        setResults(newResults);
      }
    }
    setCurrency(newCurrency);
  };

  const [formState, setFormState] = useState<CompoundInterestState>({
    initialInvestment: 1000,
    monthlyContribution: 100,
    interestRate: 7,
    years: 10,
    compoundFrequency: 12, // Monthly
  });

  const calculateCompoundBalance = (
    principal: number,
    monthlyContribution: number,
    annualRate: number,
    years: number,
    compoundsPerYear: number,
  ): number => {
    const r = annualRate / 100;
    const n = compoundsPerYear;
    const t = years;
    const PMT = monthlyContribution; // Asume contribución mensual, puede ser incorrecto si n != 12

    const ratePerPeriod = r / n;
    const totalPeriods = n * t;

    const principalFV = principal * Math.pow(1 + ratePerPeriod, totalPeriods);

    // Fórmula valor futuro anualidad (revisar si PMT siempre es mensual)
    const contributionFV =
      PMT * ((Math.pow(1 + ratePerPeriod, totalPeriods) - 1) / ratePerPeriod);

    return principalFV + contributionFV;
  };

  const [results, setResults] = useState<CalculationResults>({
    initialInvestment: 0,
    totalContributions: 0,
    interestEarned: 0,
    finalBalance: 0,
    yearlyData: Array.from({ length: 1 }, () => ({
      year: 0,
      principal: 0,
      balance: 0,
    })),
  });

  const calculateResults = () => {
    const {
      initialInvestment,
      monthlyContribution,
      interestRate,
      years,
      compoundFrequency,
    } = formState;

    const totalContributions = monthlyContribution * 12 * years;
    const finalBalance = calculateCompoundBalance(
      initialInvestment,
      monthlyContribution,
      interestRate,
      years,
      compoundFrequency,
    );
    const interestEarned =
      finalBalance - initialInvestment - totalContributions;

    const yearlyData = Array.from({ length: years + 1 }, (_, i) => {
      const principal =
        i === 0
          ? initialInvestment
          : initialInvestment + monthlyContribution * 12 * i;

      const balance =
        i === 0
          ? initialInvestment
          : calculateCompoundBalance(
              initialInvestment,
              monthlyContribution,
              interestRate,
              i,
              compoundFrequency,
            );

      return { year: i, principal, balance };
    });

    const newResults = {
      initialInvestment,
      totalContributions,
      interestEarned,
      finalBalance,
      yearlyData,
    };

    setResults(newResults);
    // Store the original results and currency
    setOriginalResults(newResults);
    setOriginalCurrency(currency);
  };

  // No initial calculation on component mount

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value === "" ? "" : parseFloat(value),
    }));
  };

  const handleSelectChange = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      compoundFrequency: parseInt(value),
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    calculateResults();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(currency === "USD" ? "en-US" : "de-DE", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleExportResults = () => {
    const executeExport = () => {
      if (results.finalBalance === 0) {
        return; // Don't export if no results
      }

      try {
        const exportData = {
          initialInvestment: formState.initialInvestment,
          monthlyContribution: formState.monthlyContribution,
          interestRate: formState.interestRate,
          years: formState.years,
          compoundFrequency: formState.compoundFrequency,
          totalContributions: results.totalContributions,
          interestEarned: results.interestEarned,
          finalBalance: results.finalBalance,
          yearlyData: results.yearlyData,
        };

        const pdf = generateCompoundInterestPDF(exportData, currency, t);
        pdf.save(`compound-interest-results-${new Date().toISOString().split('T')[0]}.pdf`);
      } catch (error) {
        console.error('Error generating PDF:', error);
        // You could add a toast notification here for better UX
      }
    };

    checkAuthAndExecute(executeExport);
  };

  // Effect to execute pending callback after successful authentication
  useEffect(() => {
    if (user && pendingCallback && !showAuthModal) {
      pendingCallback();
    }
  }, [user, pendingCallback, showAuthModal]);

  return (
    <div className="container mx-auto px-4 md:px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 text-center mb-6">
          {t("tools.compound.title")}
        </h2>
        <p className="text-neutral-600 text-center mb-12">
          {t("tools.compound.description")}
        </p>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <Card className="bg-neutral-100 w-full">
            <CardContent className="p-4 sm:p-8 space-y-6 flex flex-col items-center">
              <h3 className="font-semibold text-lg">
                {t("calculator.enterDetails")}
              </h3>

              <form onSubmit={handleSubmit} className="w-full space-y-5">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label htmlFor="initialInvestment">
                      {t("tools.compound.initialInvestment")} (
                      {currency === "USD" ? "$" : "€"})
                    </Label>
                    <CurrencySelector
                      value={currency}
                      onChange={handleCurrencyChange}
                    />
                  </div>
                  <Input
                    id="initialInvestment"
                    name="initialInvestment"
                    type="number"
                    className="calculator-input w-full border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#FF6600]"
                    value={formState.initialInvestment}
                    min="0"
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="monthlyContribution">
                    {t("tools.compound.monthlyContribution")} (
                    {currency === "USD" ? "$" : "€"})
                  </Label>
                  <Input
                    id="monthlyContribution"
                    name="monthlyContribution"
                    type="number"
                    className="calculator-input w-full border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#FF6600]"
                    value={formState.monthlyContribution}
                    min="0"
                    onChange={handleInputChange}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4 w-full">
                  <div className="flex flex-col w-full">
                    {/* Label and input wrapper */}
                    <Label htmlFor="interestRate" className="mb-1">
                      {t("tools.compound.interestRate")} (%)
                    </Label>
                    <Input
                      id="interestRate"
                      name="interestRate"
                      type="number"
                      className="calculator-input w-full border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#FF6600]"
                      value={formState.interestRate}
                      min="0.01"
                      max="100"
                      step="0.01"
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="flex flex-col min-h-[48px]">
                    {/* Label and select wrapper */}
                    <Label
                      htmlFor="compoundFrequency"
                      className="mb-1 whitespace-nowrap"
                    >
                      {t("tools.compound.frequency")}
                    </Label>
                    <Select
                      value={formState.compoundFrequency.toString()}
                      onValueChange={handleSelectChange}
                    >
                      <SelectTrigger className="calculator-input w-full h-10 border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#FF6600]" aria-label={t("tools.compound.frequency")}>
                        <SelectValue>
                          {formState.compoundFrequency === 1
                            ? t("tools.compound.annually")
                            : formState.compoundFrequency === 2
                              ? t("tools.compound.semiAnnually")
                              : formState.compoundFrequency === 4
                                ? t("tools.compound.quarterly")
                                : formState.compoundFrequency === 12
                                  ? t("tools.compound.monthly")
                                  : t("tools.compound.daily")}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">
                          {t("tools.compound.annually")}
                        </SelectItem>
                        <SelectItem value="2">
                          {t("tools.compound.semiAnnually")}
                        </SelectItem>
                        <SelectItem value="4">
                          {t("tools.compound.quarterly")}
                        </SelectItem>
                        <SelectItem value="12">
                          {t("tools.compound.monthly")}
                        </SelectItem>
                        <SelectItem value="365">
                          {t("tools.compound.daily")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="years">{t("tools.compound.years")}</Label>
                  <Input
                    id="years"
                    name="years"
                    type="number"
                    className="calculator-input w-full border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#FF6600]"
                    value={formState.years}
                    min="1"
                    max="50"
                    onChange={handleInputChange}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#FF6600] hover:bg-[#DD5500] text-white font-medium"
                >
                  {t("common.calculate")}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div>
            <Card className="bg-white p-6 border border-neutral-200 shadow-sm mb-6">
              <h3 className="font-semibold text-lg mb-4">
                {t("common.results")}
              </h3>

              {results.finalBalance === 0 ? (
                <div className="space-y-3">
                  <div className="text-center py-3 text-neutral-500 mb-2">
                    {t("tools.compound.calculateToSeeResults")}
                  </div>

                  <div className="flex justify-between items-center gap-4 py-2 border-b border-neutral-200">
                    <span className="text-neutral-600 whitespace-nowrap">
                      {t("tools.compound.initialInvestment")}:
                    </span>
                    <span className="font-medium text-right break-all">—</span>
                  </div>

                  <div className="flex justify-between items-center gap-4 py-2 border-b border-neutral-200">
                    <span className="text-neutral-600 whitespace-nowrap">
                      {t("tools.compound.contributions")}:
                    </span>
                    <span className="font-medium text-right break-all">—</span>
                  </div>

                  <div className="flex justify-between items-center gap-4 py-2 border-b border-neutral-200">
                    <span className="text-neutral-600 whitespace-nowrap">
                      {t("tools.compound.interestEarned")}:
                    </span>
                    <span className="font-medium text-green-500 text-right break-all">
                      —
                    </span>
                  </div>

                  <div className="flex justify-between items-center gap-4 py-2">
                    <span className="text-neutral-700 font-semibold whitespace-nowrap">
                      {t("tools.compound.finalBalance")}:
                    </span>
                    <span className="font-bold text-xl text-[#FF6600] text-right break-all">
                      —
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center gap-4 py-2 border-b border-neutral-200">
                    <span className="text-neutral-600 whitespace-nowrap">
                      {t("tools.compound.initialInvestment")}:
                    </span>
                    <span className="font-medium text-right break-all">
                      {formatCurrency(results.initialInvestment)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center gap-4 py-2 border-b border-neutral-200">
                    <span className="text-neutral-600 whitespace-nowrap">
                      {t("tools.compound.contributions")}:
                    </span>
                    <span className="font-medium text-right break-all">
                      {formatCurrency(results.totalContributions)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center gap-4 py-2 border-b border-neutral-200">
                    <span className="text-neutral-600 whitespace-nowrap">
                      {t("tools.compound.interestEarned")}:
                    </span>
                    <span className="font-medium text-green-500 text-right break-all">
                      {formatCurrency(results.interestEarned)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center gap-4 py-2">
                    <span className="text-neutral-700 font-semibold whitespace-nowrap">
                      {t("tools.compound.finalBalance")}:
                    </span>
                    <span className="font-bold text-xl text-[#FF6600] text-right break-all">
                      {formatCurrency(results.finalBalance)}
                    </span>
                  </div>
                </div>
              )}
            </Card>

            <Card className="bg-white p-4 border border-neutral-200 shadow-sm">
              {results.finalBalance === 0 ? (
                <div className="text-center py-8 opacity-60">
                  <p className="text-neutral-500">
                    {t("tools.compound.calculateToSeeCharts")}
                  </p>
                </div>
              ) : (
                <CompoundInterestChart
                  data={results.yearlyData}
                  currency={currency}
                />
              )}
            </Card>
          </div>
        </div>

        <div className="text-center">
          <Button
            variant="ghost"
            className={`inline-flex items-center font-medium ${
              results.finalBalance === 0 || isCheckingAuth
                ? "text-neutral-400 cursor-not-allowed"
                : "text-neutral-700 hover:text-[#FF6600] cursor-pointer"
            }`}
            onClick={handleExportResults}
            disabled={results.finalBalance === 0 || isCheckingAuth}
          >
            <Download className="mr-1 h-4 w-4" /> 
            {isCheckingAuth ? t("common.loading") : t("common.exportResults")}
            {!user && (
              <span className="ml-1 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                {t("common.loginRequired")}
              </span>
            )}
          </Button>

          {/* Google Ad Placeholder */}
          {/* <AdPlaceholder className="mt-12"/>*/}
        </div>

        {/* Educational Content Section */}
        <div className="mt-16 space-y-12">
          {/* How it works */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.howItWorksTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.compound.howItWorksDesc1")}
              </p>
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.compound.howItWorksDesc2")}
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-6">
                <p className="text-neutral-800 font-medium">
                  {t("tools.compound.howItWorksExample")}
                </p>
              </div>
              <p className="text-lg text-neutral-700 leading-relaxed">
                {t("tools.compound.howItWorksDesc3")}
              </p>
            </div>
          </div>

          {/* Formula Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.formulaTitle")}
            </h2>
            <div className="relative bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 rounded-xl p-8 border border-slate-200">
              {/* Formula Box */}
              <div className="relative bg-white rounded-xl p-8 mb-8 text-center shadow-lg border-2 border-[#FF6600]">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF6600] text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Fórmula
                </div>
                <p className="text-3xl font-mono font-bold text-neutral-900 tracking-wide">
                  C<sub className="text-lg">n</sub> = C<sub className="text-lg">0</sub> (1 + i)<sup className="text-lg">n</sup>
                </p>
              </div>
              
              {/* Variables Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#FF6600] transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <span className="font-mono font-bold text-white text-sm">Cn</span>
                    </div>
                    <p className="text-sm font-semibold text-neutral-900">
                      {t("tools.compound.formulaCn")}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#FF6600] transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                      <span className="font-mono font-bold text-white text-sm">C0</span>
                    </div>
                    <p className="text-sm font-semibold text-neutral-900">
                      {t("tools.compound.formulaC0")}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#FF6600] transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <span className="font-mono font-bold text-white text-sm">n</span>
                    </div>
                    <p className="text-sm font-semibold text-neutral-900">
                      {t("tools.compound.formulaN")}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-[#FF6600] transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#FF6600] to-[#FF7700] rounded-lg flex items-center justify-center">
                      <span className="font-mono font-bold text-white text-sm">i</span>
                    </div>
                    <p className="text-sm font-semibold text-neutral-900">
                      {t("tools.compound.formulaI")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Example Box */}
              <div className="bg-gradient-to-r from-[#FF6600]/10 via-orange-50/50 to-[#FF6600]/10 rounded-lg p-6 border-l-4 border-[#FF6600]">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#FF6600] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">💡</span>
                  </div>
                  <p className="text-neutral-800 leading-relaxed">
                    {t("tools.compound.formulaExample")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Ways to use calculator */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.waysToUseTitle")}
            </h2>
            <div className="space-y-6">
              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.compound.way1Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.way1Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.compound.way2Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.way2Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 pb-6 border-b border-neutral-200">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.compound.way3Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.way3Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">
                    {t("tools.compound.way4Title")}
                  </h3>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.way4Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Investment Tips */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.investmentTipsTitle")}
            </h2>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.compound.tip1Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.tip1Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.compound.tip2Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.tip2Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.compound.tip3Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.tip3Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Where to Invest Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.whereToInvestTitle")}
            </h2>
            <p className="text-lg text-neutral-700 leading-relaxed mb-8">
              {t("tools.compound.whereToInvestIntro")}
            </p>

            {/* Index Funds */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">📈</span>
                {t("tools.compound.indexFundsTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.compound.indexFundsDesc1")}
              </p>
              <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg p-5 mb-4 border-l-4 border-blue-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.compound.practicalExample")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.compound.indexFundsExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.compound.indexFundsDesc2")}
              </p>
            </div>

            {/* ETFs */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">📊</span>
                {t("tools.compound.etfsTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.compound.etfsDesc1")}
              </p>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 rounded-lg p-5 mb-4 border-l-4 border-purple-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.compound.practicalExample")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.compound.etfsExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.compound.etfsDesc2")}
              </p>
            </div>

            {/* Stocks */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">💹</span>
                {t("tools.compound.stocksTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.compound.stocksDesc1")}
              </p>
              <div className="bg-gradient-to-r from-green-50 to-green-100/50 rounded-lg p-5 mb-4 border-l-4 border-green-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.compound.practicalExample")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.compound.stocksExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.compound.stocksDesc2")}
              </p>
            </div>

            {/* Cryptocurrencies */}
            <div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">💰</span>
                {t("tools.compound.cryptoTitle")}
              </h3>
              <p className="text-neutral-700 leading-relaxed mb-4">
                {t("tools.compound.cryptoDesc1")}
              </p>
              <div className="bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-lg p-5 mb-4 border-l-4 border-orange-500">
                <p className="font-semibold text-neutral-900 mb-2 text-sm">
                  {t("tools.compound.practicalExample")}
                </p>
                <p className="text-neutral-800">
                  {t("tools.compound.cryptoExample")}
                </p>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.compound.cryptoDesc2")}
              </p>
            </div>
          </div>

          {/* Best Brokers Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.bestBrokersTitle")}
            </h2>
            <p className="text-lg text-neutral-700 leading-relaxed mb-8">
              {t("tools.compound.bestBrokersIntro")}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Trade Republic */}
              <a 
                href="https://traderepublic.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block bg-white rounded-lg p-6 border border-neutral-200 hover:border-[#FF6600] transition-colors cursor-pointer hover:shadow-lg no-underline"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center p-2">
                    <img 
                      src="https://cdn.brandfetch.io/id5mURhE1s/theme/dark/symbol.svg?c=1bxid64Mup7aczewSAYMX&t=1695070244740" 
                      alt="Trade Republic logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900">
                      Trade Republic
                    </h3>
                    <p className="text-xs font-semibold text-slate-600">
                      {t("tools.compound.tradeRepublicSubtitle")}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-green-500 text-sm mt-0.5">✓</span>
                    <p className="text-sm text-neutral-700">{t("tools.compound.trFeature1")}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-500 text-sm mt-0.5">✓</span>
                    <p className="text-sm text-neutral-700">{t("tools.compound.trFeature2")}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-500 text-sm mt-0.5">✓</span>
                    <p className="text-sm text-neutral-700">{t("tools.compound.trFeature3")}</p>
                  </div>
                </div>
                <p className="text-neutral-600 leading-relaxed text-sm">
                  {t("tools.compound.trDescription")}
                </p>
              </a>

              {/* MyInvestor */}
              <a 
                href="https://myinvestor.es" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block bg-white rounded-lg p-6 border border-neutral-200 hover:border-[#FF6600] transition-colors cursor-pointer hover:shadow-lg no-underline"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <defs>
                        <linearGradient id="myinvestor-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" style={{stopColor:'#6366F1', stopOpacity:1}} />
                          <stop offset="33%" style={{stopColor:'#B745FF', stopOpacity:1}} />
                          <stop offset="66%" style={{stopColor:'#EF4444', stopOpacity:1}} />
                          <stop offset="100%" style={{stopColor:'#FF9B37', stopOpacity:1}} />
                        </linearGradient>
                      </defs>
                      <circle cx="50" cy="50" r="48" fill="none" stroke="url(#myinvestor-grad)" strokeWidth="4"/>
                      <text x="50" y="62" fontFamily="Arial, sans-serif" fontSize="36" fontWeight="bold" textAnchor="middle" fill="#2D3A3F">my</text>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900">
                      MyInvestor
                    </h3>
                    <p className="text-xs font-semibold text-blue-600">
                      {t("tools.compound.myInvestorSubtitle")}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2">
                    <span className="text-green-500 text-sm mt-0.5">✓</span>
                    <p className="text-sm text-neutral-700">{t("tools.compound.miFeature1")}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-500 text-sm mt-0.5">✓</span>
                    <p className="text-sm text-neutral-700">{t("tools.compound.miFeature2")}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-500 text-sm mt-0.5">✓</span>
                    <p className="text-sm text-neutral-700">{t("tools.compound.miFeature3")}</p>
                  </div>
                </div>
                <p className="text-neutral-600 leading-relaxed text-sm">
                  {t("tools.compound.miDescription")}
                </p>
              </a>
            </div>
          </div>

          {/* How to Invest Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.howToInvestTitle")}
            </h2>
            <p className="text-lg text-neutral-700 leading-relaxed mb-5">
              {t("tools.compound.howToInvestDesc1")}
            </p>
            <p className="text-lg text-neutral-700 leading-relaxed mb-6">
              {t("tools.compound.howToInvestDesc2")}
            </p>
            <div className="bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-lg p-5 border-l-4 border-[#FF6600]">
              <p className="font-semibold text-neutral-900 mb-2">
                {t("tools.compound.keyTip")}
              </p>
              <p className="text-neutral-700 leading-relaxed">
                {t("tools.compound.howToInvestKeyTip")}
              </p>
            </div>
          </div>

          {/* Real Examples Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.realExamplesTitle")}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Example 1 */}
              <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl p-6 border border-blue-200 hover:border-blue-400 transition-all hover:shadow-lg flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4 min-h-[100px]">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl">👤</span>
                  </div>
                  <h4 className="text-lg font-bold text-neutral-900 leading-tight">
                    {t("tools.compound.example1Title")}
                  </h4>
                </div>
                <p className="text-neutral-700 leading-relaxed mb-4 text-sm flex-grow">
                  {t("tools.compound.example1Desc")}
                </p>
                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-300">
                  <p className="text-xs font-semibold text-blue-700 mb-1">{t("tools.compound.result")}</p>
                  <p className="text-2xl font-bold text-blue-600">{t("tools.compound.example1Result")}</p>
                </div>
              </div>

              {/* Example 2 */}
              <div className="bg-gradient-to-br from-white to-orange-50/30 rounded-xl p-6 border border-orange-200 hover:border-[#FF6600] transition-all hover:shadow-lg flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4 min-h-[100px]">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FF6600] to-[#FF7700] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl">🕐</span>
                  </div>
                  <h4 className="text-lg font-bold text-neutral-900 leading-tight">
                    {t("tools.compound.example2Title")}
                  </h4>
                </div>
                <p className="text-neutral-700 leading-relaxed mb-4 text-sm flex-grow">
                  {t("tools.compound.example2Desc")}
                </p>
                <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-300">
                  <p className="text-xs font-semibold text-orange-700 mb-1">{t("tools.compound.result")}</p>
                  <p className="text-2xl font-bold text-[#FF6600]">{t("tools.compound.example2Result")}</p>
                </div>
              </div>

              {/* Example 3 */}
              <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl p-6 border border-green-200 hover:border-green-400 transition-all hover:shadow-lg flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4 min-h-[100px]">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl">⚡</span>
                  </div>
                  <h4 className="text-lg font-bold text-neutral-900 leading-tight">
                    {t("tools.compound.example3Title")}
                  </h4>
                </div>
                <p className="text-neutral-700 leading-relaxed mb-4 text-sm flex-grow">
                  {t("tools.compound.example3Desc")}
                </p>
                <div className="bg-green-500/10 rounded-lg p-4 border border-green-300">
                  <p className="text-xs font-semibold text-green-700 mb-1">{t("tools.compound.result")}</p>
                  <p className="text-2xl font-bold text-green-600">{t("tools.compound.example3Result")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Common Mistakes Section */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.compound.commonMistakesTitle")}
            </h2>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.compound.mistake1Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.mistake1Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.compound.mistake2Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.mistake2Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.compound.mistake3Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.compound.mistake3Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Content Section */}
        <div className="mt-16 space-y-12">
          {/* Other Calculators Section */}
          <div className="bg-gradient-to-br from-neutral-50 to-white rounded-xl p-8 border border-neutral-200">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.compound.otherCalculators")}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <LanguageLink
                to="/tools/monthly-savings-calculator"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.compound.monthlySavingsCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.monthlySavingsDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/mortgage-calculator"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.compound.mortgageCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.mortgageDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/net-salary"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.compound.salaryCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.salaryDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/salary-comparison"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.compound.salaryComparisonCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.compound.salaryComparisonDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 border border-blue-200">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.compound.relatedArticles")}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* First Article - Takes 2 columns */}
              <LanguageLink
                to="/resources/start-investing-article"
                className="md:col-span-2 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?q=80&w=800&auto=format&fit=crop" 
                  alt="Young professional starting investment journey with laptop showing financial dashboard and growth charts"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                      {t("tools.compound.article1Title")}
                    </h3>
                    <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                      {t("tools.compound.article1Desc")}
                    </p>
                    <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                      {t("tools.compound.readMore")}
                      <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </LanguageLink>

              {/* Second Article */}
              <LanguageLink
                to="/resources/risk-return-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?q=80&w=500&auto=format&fit=crop" 
                  alt="Risk vs return investment concept"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.compound.article2Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.compound.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Third Article */}
              <LanguageLink
                to="/resources/etf-index-funds-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1642543492481-44e81e3914a7?q=80&w=500&auto=format&fit=crop" 
                  alt="Modern ETF and index fund investment portfolio with digital charts and analytics"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.compound.article3Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.compound.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Fourth Article - Spans remaining space */}
              <LanguageLink
                to="/resources/investing-mistakes-article"
                className="md:col-span-2 lg:col-span-4 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[200px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=800&auto=format&fit=crop" 
                  alt="Investing mistakes concept"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30 group-hover:from-black/80 group-hover:via-black/50 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-center items-start max-w-2xl">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                    {t("tools.compound.article4Title")}
                  </h3>
                  <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                    {t("tools.compound.article4Desc")}
                  </p>
                  <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.compound.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>
            </div>
          </div>
        </div>
      </div>

      {/* Authentication Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        mode="login" 
      />
    </div>
  );
};

export default CompoundInterestCalculator;
